/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/directDialApi.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    API calls for Direct Dial operations.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { DirectDial, DirectDialPayload } from '../types';

export interface ListParams {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  domainId?: number;
}

export interface ListResponse {
  data: DirectDial[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export async function fetchDirectDials(params: ListParams): Promise<ListResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search && { search: params.search })
  });

  // Add domain filter if provided
  if (params.domainId) {
    queryParams.append('domainId', params.domainId.toString());
  }

  const response = await fetch(
    buildApiUrl(`/api/telephony/direct-dial?${queryParams}`),
    { headers: getApiHeaders() }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  const result = await response.json();

  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch direct dials');
  }

  return {
    data: result.data || [],
    pagination: result.pagination || {
      page: params.page,
      limit: params.limit,
      total: 0,
      totalPages: 0
    }
  };
}

export async function fetchDirectDialById(id: string): Promise<DirectDial> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/direct-dial/${id}`),
    { headers: getApiHeaders() }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  const result = await response.json();

  if (!result?.success || !result.data) {
    throw new Error(result?.message || 'Direct dial not found');
  }

  return result.data;
}

export async function createDirectDial(payload: DirectDialPayload): Promise<DirectDial> {
  const response = await fetch(
    buildApiUrl('/api/telephony/direct-dial'),
    {
      method: 'POST',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  const result = await response.json();

  if (!result?.success || !result.data) {
    throw new Error(result?.message || 'Failed to create direct dial');
  }

  return result.data;
}

export async function updateDirectDial(id: string, payload: DirectDialPayload): Promise<DirectDial> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/direct-dial/${id}`),
    {
      method: 'PUT',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  const result = await response.json();

  if (!result?.success) {
    throw new Error(result?.message || 'Failed to update direct dial');
  }

  // Handle cases where API returns { data: {...} } or {...} directly
  // If no data is returned (update case), return the original data with the ID
  return result.data || result || { ...payload, id: id as string };
}

export async function deleteDirectDial(id: string): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/direct-dial/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  const result = await response.json();

  if (!result?.success) {
    throw new Error(result?.message || 'Failed to delete direct dial');
  }
}

